import { useContext } from 'react';
import logger from '../providers/logger';
import { SettingsContext } from '../context/SettingsContext';

/**
 * Use logging Application Insights logging and local trace logging.
 * @returns
 */
export const useLogger = (prefix: string = '') => {
  const { traceLog, setTraceLog, settings } = useContext(SettingsContext);

  /**
   * add messages to the trace log
   * If debugMode is enabled with traceLogToConsole setting, also use console.log
   * @param messages
   */
  const log = (...messages: unknown[]) => {
    if (settings?.debugMode && settings.traceLogToConsole) {
      if (prefix) {
        console.log(`[${prefix}]`, ...messages);
      } else {
        console.log(...messages);
      }
    }

    add(stringifyMessages(...messages));
  };

  /**
   * add messages to the trace log
   * If debugMode is enabled with traceLogToConsole setting, also use console.warn
   * @param messages
   */
  const warn = (...messages: unknown[]) => {
    if (settings?.debugMode && settings.traceLogToConsole) {
      if (prefix) {
        console.warn(`[${prefix}]`, ...messages);
      } else {
        console.warn(...messages);
      }
    }

    add(stringifyMessages(...messages));
  };

  /**
   * add messages to the trace log
   * If debugMode is enabled with traceLogToConsole setting, also use console.error
   * @param messages
   */
  const error = (...messages: unknown[]) => {
    if (settings?.debugMode && settings.traceLogToConsole) {
      if (prefix) {
        console.error(`[${prefix}]`, ...messages);
      } else {
        console.error(...messages);
      }
    }

    add(stringifyMessages(...messages), true);
  };

  const stringifyMessages = (...messages: unknown[]) => {
    const formattedMessages = messages.map((msg) => {
      try {
        if (msg === undefined || msg === null) {
          return typeof msg;
        } else if (typeof msg === 'function') {
          return `Function: ${msg.name || 'anonymous'}`;
        } else if (typeof msg === 'object') {
          try {
            return JSON.stringify(msg, null, 1);
          } catch (error: unknown) {
            return `Error stringifying object: ${(error as Error)?.message}`;
          }
        } else {
          return msg.toString();
        }
      } catch (ex: unknown) {
        return `Error converting message to string: ${(ex as Error)?.message}`;
      }
    });
    return `${prefix ? `[${prefix}] ` : ''}${formattedMessages.join('')}`;
  };

  // internal function to add messages to the trace log
  const add = (message: string, uploadToAI: boolean = false) => {
    if (!message) {
      return;
    }

    setTraceLog((prevTraceLog: string[]) => {
      const newTraceLog = [...prevTraceLog, message];
      return newTraceLog.length > 1250 ? newTraceLog.slice(-1000) : newTraceLog;
    });
    if (uploadToAI) {
      try {
        logger.error(message);
      } catch (error: unknown) {
        // Do not fail on upload
      }
    }
  };

  /**
   * Get all trace log entries
   * @returns
   */
  const get = () => {
    return traceLog;
  };

  /**
   * Clear all trace log entries
   * @returns
   */
  const clear = () => {
    setTraceLog([]);
  };

  /**
   * Upload trace log entries to Application Insights
   * @returns
   */
  const uploadTraceLogs = () => {
    try {
      logger.info(`UI Trace Log:\n${traceLog.join('\n')}`);
    } catch (error: unknown) {
      //do not fail on upload
      return;
    }

    clear();
  };

  /**
   * Application Insights logging.
   */
  const AI = {
    info: (message: string): void => {
      log(message);
      try {
        logger.info(`${message}`);
      } catch (error: unknown) {
        //do not fail on upload
        logger.error(`Error uploading to Application Insights: ${error}`);
      }
    },
    warning: (message: string): void => {
      warn(message);
      try {
        logger.warning(`${message}`);
      } catch (error: unknown) {
        //do not fail on upload
        logger.error(`Error uploading to Application Insights: ${error}`);
      }
    },
    error: (message: string) => {
      error(message);
      try {
        logger.error(`${message}`);
      } catch (error: unknown) {
        //do not fail on upload
        logger.error(`Error uploading to Application Insights: ${error}`);
      }
    },
    event: logger.event,
  };

  /**
   * Local trace logging: add, log, warn, error, get, clear, uploadTraceLogs
   */
  const UI = { log, warn, error, get, clear, uploadTraceLogs };

  return {
    AI,
    UI,
  };
};
